import React, { useCallback, useMemo } from 'react';

import { LayoutChangeEvent, useWindowDimensions } from 'react-native';

import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import { DeliveryFeesStripe } from 'components/delivery-fees-stripe';
import { FullWidthAutoHeightImage } from 'components/picture';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useScrollResetOnMount } from 'state/scroll/hooks';
import { primitive } from 'styles/constants';
import { hasExcessSodium } from 'utils/menu/sodium';
import { TraceName, stopTrace } from 'utils/performance/trace';
import { getSelectionsFromMenuData } from 'utils/wizard';

import SodiumFlag from '../nutrition-information/sodium-flag';
import { PriceAndCalories } from '../price-and-calories';

import { MOBILE_SAFETY_MEASURE } from './constants';
import { CurvedBackground } from './curved-bg';
import { ProductImageWrapper, ProductInformationContainer, StyledProductPicture } from './styled';

const sodiumFlagIcon = <SodiumFlag size={{ base: '18px', md: '24px' }} />;

// OLD - LEGACY
export const ProductOverview: React.FC<React.PropsWithChildren<{ onImageLoaded?: () => void }>> = ({
  onImageLoaded,
}) => {
  const [bgHeight, setBgHeight] = React.useState<number>(0);
  const { menuObject, selectedProduct, userSelections } = useProductWizardContext();
  const takeoverPdp = useAppTakeoverPdp(menuObject?._id);

  useScrollResetOnMount();

  const isDesktop = useIsDesktopBreakpoint();
  const { height } = useWindowDimensions();

  const forcePickerImage = useFlag(LaunchDarklyFlag.ENABLE_FORCE_PICKER_IMAGE);

  const isOverSodiumLimit = useMemo(() => {
    const { comboSlotSelections, modifierSelections } = getSelectionsFromMenuData({
      data: menuObject,
      pickerSelectionOverrides: userSelections.pickerAspects,
    });

    return hasExcessSodium({
      selectedOpt: selectedProduct,
      comboSlotSelections,
      modifierSelections,
    });
  }, [menuObject, selectedProduct, userSelections.pickerAspects]);

  const image = forcePickerImage ? menuObject.image : selectedProduct.image;

  const onImageDisplay = useCallback(() => {
    stopTrace(TraceName.MENU_PDP_LOAD, { menuItemId: menuObject?._id, isOneMenu: 'false' });
  }, [menuObject?._id]);

  return (
    <>
      <ProductImageWrapper background={takeoverPdp?.backgroundColor}>
        {takeoverPdp.isTakeoverActive ? (
          <>
            <FullWidthAutoHeightImage
              source={takeoverPdp?.overviewBottomImage}
              style={{ position: 'absolute', top: takeoverPdp?.overviewBottomImageOffset }}
            />
            <FullWidthAutoHeightImage
              source={takeoverPdp?.overviewTopImage}
              style={{ position: 'absolute' }}
            />
          </>
        ) : (
          <CurvedBackground
            height={bgHeight}
            curveColor={primitive.$white}
            backgroundColor={primitive.$mayoEggWhite}
          />
        )}

        <StyledProductPicture
          key={image?.asset?._id ?? 'product-image'}
          image={image}
          alt={menuObject?.imageDescription?.locale || ''}
          width={height / (isDesktop ? 2 : 2.5)}
          onImageLoaded={onImageLoaded}
          onDisplay={onImageDisplay}
          onLayout={({ nativeEvent }: LayoutChangeEvent) => {
            const { height } = nativeEvent.layout;
            // Sets the CurvedMayoBgColor height to be: (img height - 40% of the img height)
            // We will be updating this logic base on design needs, leaving this calculation for now.
            // The Main idea here is to have the background svg curved effect.
            const bgHeight = Math.floor(height - height * 0.4);
            setBgHeight(bgHeight);
          }}
          {...(!isDesktop && { minHeight: MOBILE_SAFETY_MEASURE })}
        />
      </ProductImageWrapper>

      <ProductInformationContainer>
        <PriceAndCalories
          fontColorOverride={takeoverPdp?.priceAndCaloriesFontColor}
          productPriceConditions={{
            shouldIncludeModifications: true,
            shouldIncludeQuantity: false,
          }}
          icon={isOverSodiumLimit ? sodiumFlagIcon : undefined}
        />

        {/* @TODO: Do we want to keep this in here ? */}
        <DeliveryFeesStripe />
      </ProductInformationContainer>
    </>
  );
};
